import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, Field } from "formik";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
	TextField
} from '@material-ui/core';
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: { display: "flex", flexWrap: "wrap" },

	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": { marginRight: theme.spacing(1), },
	},

  multFieldLineButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& > *:not(:last-child)": { marginRight: theme.spacing(1), },
  },

	btnWrapper: { position: "relative" },

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},

	formControl: { margin: theme.spacing(1), minWidth: 120 },

	floatingButton: {
		transition: "transform 0.3s",
		"&:hover": { transform: "translateY(-5px)" },
	}
}));

const ManagementModal = ({ open, onClose, client }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();

  const initialState = useMemo(() => {
    return {
      id: 0,
      name: "",
      url: "",
      totalUsers: 0,
      totalChips: 0,
      dispatchEnabledOnBestzap: false,
      isMetaApiEnabled: false,
      isActive: false,
      isActiveMessageType: null,
      importMessages: "enabled",
      isFullMessageImportation: false,
      importMessagesMode: 1,
    }
  }, []);

  const [selectedClient, setSelectedClient] = useState(client || initialState);



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (!open) return;
    setSelectedClient(client || initialState);
  }, [open, client, initialState]);

  

  //  ***************
  //  ** Functions **
  //  ***************
  const handleClose = () => {
    onClose();
    setSelectedClient(initialState);
  };

  const handleSaveClient = async (values) => {
    const accountId = client.id;
    const totalUsers = parseInt(values.totalUsers, 10);
    const totalChips = parseInt(values.totalChips, 10);
    const isActive = values.isActive;
    const isActiveMessageType = isActive ? null : values.isActiveMessageType;
    const dispatchEnabledOnBestzap = values.dispatchEnabledOnBestzap;
    const isMetaApiEnabled = values.isMetaApiEnabled;
    const importMessagesMode = values.importMessagesMode;

    if (totalUsers === 0 || isNaN(totalUsers)) {
      toast.info(i18n.t("managementModal.toasts.totalUsersZero"));
      return;
    }

    if (totalChips === 0 || isNaN(totalChips)) {
      toast.info(i18n.t("managementModal.toasts.totalChipsZero"));
      return;
    }

    if (!isActive && ![1, 2].includes(isActiveMessageType)) {
      toast.info(i18n.t("managementModal.toasts.isActiveMessageTypeEmpty"));
      return;
    }

    try {
      await api.put(`/accounts/${accountId}`, {
        totalUsers
        , totalChips
        , isActive
        , isActiveMessageType
        , dispatchEnabledOnBestzap
        , isMetaApiEnabled
        , importMessagesMode
      });

      handleClose();
    } catch (exception) {
      console.log("Handle Save Client Exception:", exception);
      toastError(exception);
    }
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        scroll="paper"
        fullWidth
      >
        <DialogTitle>
          {client ? i18n.t("managementModal.title.edit") : i18n.t("managementModal.title.add")}
        </DialogTitle>

        <Formik
          initialValues={selectedClient}
          enableReinitialize={true}
          onSubmit={(values, actions) => {
            setTimeout(async () => {
              await handleSaveClient(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                {client && (
                  <div className={classes.multFieldLine}>
                    <Field
                      as={TextField}
                      label={i18n.t("managementModal.form.tenantId")}
                      name="id"
                      variant="outlined"
                      margin="dense"
                      inputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </div>
                )}

                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("managementModal.form.name")}
                    name="name"
                    variant="outlined"
                    margin="dense"
                    inputProps={{ readOnly: !!client }}
                    autoFocus={!!!client}
                    fullWidth
                  />
                </div>

                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("managementModal.form.url")}
                    name="url"
                    variant="outlined"
                    margin="dense"
                    inputProps={{ readOnly: !!client }}
                    fullWidth
                  />
                </div>

                <div className={classes.multFieldLine}>
                  <Field
                    as={NumericFormat}
                    customInput={TextField}
                    variant="outlined"
                    margin="dense"
                    name="totalUsers"
                    autoFocus={!!client}
                    allowNegative={false}
                    allowLeadingZeros={false}
                    decimalScale={0}
                    label={i18n.t("managementModal.form.totalUsers")}
                    inputProps={{ inputMode: "numeric" }}
                    error={touched.totalUsers && Boolean(errors.totalUsers)}
                    helperText={touched.totalUsers && errors.totalUsers}
                    fullWidth
                  />

                  <Field
                    as={NumericFormat}
                    customInput={TextField}
                    variant="outlined"
                    margin="dense"
                    name="totalChips"
                    autoFocus={false}
                    allowNegative={false}
                    allowLeadingZeros={false}
                    decimalScale={0}
                    label={i18n.t("managementModal.form.totalChips")}
                    inputProps={{ inputMode: "numeric" }}
                    error={touched.totalChips && Boolean(errors.totalChips)}
                    helperText={touched.totalChips && errors.totalChips}
                    fullWidth
                  />
                </div>

                <div className={classes.multFieldLine}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>{i18n.t("managementModal.form.importMessagesMode")}</InputLabel>

                    <Field
                      as={Select}
                      label={i18n.t("managementModal.form.importMessagesMode")}
                      name="importMessagesMode"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    >
                      <MenuItem value={0}>{i18n.t("managementModal.form.select.importMessagesModeDisabled")}</MenuItem>
                      <MenuItem value={1}>{i18n.t("managementModal.form.select.importMessagesModeRecent")}</MenuItem>
                      <MenuItem value={2}>{i18n.t("managementModal.form.select.importMessagesModeOnDemand")}</MenuItem>
                    </Field>
                  </FormControl>
                </div>

                <br />
                <Divider />
                <br />

                <div className={classes.multFieldLine}>
                  <FormControlLabel
                    control={<Field as={Switch} color="primary" name="isActive" checked={values.isActive} />}
                    label={i18n.t("managementModal.form.isActive")}
                  />
                </div>

                <div className={classes.multFieldLine}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>{i18n.t("managementModal.form.isActiveMessageType")}</InputLabel>

                    <Field
                      as={Select}
                      label={i18n.t("managementModal.form.isActiveMessageType")}
                      name="isActiveMessageType"
                      variant="outlined"
                      margin="dense"
                      disabled={values.isActive}
                      fullWidth
                    >
                      <MenuItem value={1}>{i18n.t("managementModal.form.select.blocking")}</MenuItem>
                      <MenuItem value={2}>{i18n.t("managementModal.form.select.nonPayment")}</MenuItem>
                    </Field>
                  </FormControl>
                </div>
              </DialogContent>

              <DialogActions>
                <Button
                  color="inherit"
                  variant="outlined"
                  disabled={isSubmitting}
                  className={classes.floatingButton}
                  onClick={handleClose}
                >
                  {i18n.t("managementModal.buttons.cancel")}
                </Button>

                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                  className={`${classes.btnWrapper} ${classes.floatingButton}`}
                >
                  {client ? i18n.t("managementModal.buttons.okEdit") : i18n.t("managementModal.buttons.okAdd")}
                  {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ManagementModal;
